import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { collection, getDocs, type DocumentData } from 'firebase/firestore';
import { db } from '../../../lib/firebase';
import { errorText } from '../../../lib/error-text';
import { useTenantStore } from '../../../stores/tenant.store';
import {
  groupAccessApi,
  type AccessGroup,
  type AccessPreviewDto,
  type AccessRowDto,
  type AccessSkipReason,
} from '../../../lib/api/group-access.api';

// ─── Dar acceso a un grupo (H10) ────────────────────────────────────────────
// 1) Se elige el grupo: los profesores con sección en un período (la opción
//    principal) o todos los profesores —los dos, solo administración—, los
//    alumnos de un período o los de un programa. 2) El servidor dice a quién sí y a quién no
//    (sin correo, correo provisorio, fallecido, correo compartido, ya tiene
//    acceso). 3) Se confirma ESCRIBIENDO la cifra. 4) Tandas de 10 con avance
//    y un resumen al final. Ninguna invitación real se envía sin el OK
//    explícito de Ricardo para ese grupo (regla del PM): la cifra escrita y la
//    auditoría de cada sesión lo respaldan.

type GroupKind = AccessGroup['kind'];

const SKIP_KEYS: Record<AccessSkipReason, string> = {
  no_email: 'people.groupAccess.skip.no_email',
  placeholder_email: 'people.groupAccess.skip.placeholder_email',
  deceased: 'people.groupAccess.skip.deceased',
  email_shared: 'people.groupAccess.skip.email_shared',
  already_has_access: 'people.groupAccess.skip.already_has_access',
};

export function GroupAccessDialog({
  open,
  onClose,
  canInviteFaculty,
}: {
  open: boolean;
  onClose: () => void;
  /** Dar acceso a profesores es de la administración. */
  canInviteFaculty: boolean;
}): JSX.Element {
  const { t } = useTranslation('sis');
  const { currentTenantId } = useTenantStore();
  const [kind, setKind] = useState<GroupKind>(canInviteFaculty ? 'termFaculty' : 'term');
  const [targetId, setTargetId] = useState('');
  const [terms, setTerms] = useState<DocumentData[]>([]);
  const [programs, setPrograms] = useState<DocumentData[]>([]);
  const [preview, setPreview] = useState<AccessPreviewDto | null>(null);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [rows, setRows] = useState<AccessRowDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !currentTenantId) return;
    void Promise.all([
      getDocs(collection(db, `tenants/${currentTenantId}/terms`)),
      getDocs(collection(db, `tenants/${currentTenantId}/programs`)),
    ]).then(([ts, ps]) => {
      // El más reciente primero: en un seminario con años de historia, el
      // período que se busca está arriba.
      setTerms(
        ts.docs
          .map((d) => ({ id: d.id, ...d.data() }) as DocumentData)
          .sort((a, b) => String(b.startDate ?? '').localeCompare(String(a.startDate ?? ''))),
      );
      setPrograms(ps.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
  }, [open, currentTenantId]);

  const reset = () => {
    setPreview(null);
    setTyped('');
    setRows(null);
    setProgress(null);
    setError(null);
  };
  const close = () => {
    if (busy) return;
    reset();
    onClose();
  };

  const group = (): AccessGroup | null =>
    kind === 'faculty' ? { kind } : targetId ? { kind, id: targetId } : null;
  const needsTerm = kind === 'term' || kind === 'termFaculty';

  const loadPreview = async () => {
    const g = group();
    if (!currentTenantId || !g) return;
    setBusy(true);
    setError(null);
    try {
      setPreview(await groupAccessApi.preview({ tenantId: currentTenantId, group: g }));
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const grant = async () => {
    const g = group();
    if (!currentTenantId || !g || !preview) return;
    setBusy(true);
    setError(null);
    try {
      const started = await groupAccessApi.start({
        tenantId: currentTenantId,
        group: g,
        confirmCount: Number(typed),
      });
      const all: AccessRowDto[] = [];
      setProgress({ done: 0, total: started.personIds.length });
      for (let i = 0; i < started.personIds.length; i += started.batch) {
        const chunk = started.personIds.slice(i, i + started.batch);
        const r = await groupAccessApi.run({
          tenantId: currentTenantId,
          sessionId: started.sessionId,
          personIds: chunk,
        });
        all.push(...r.rows);
        setProgress({
          done: Math.min(i + chunk.length, started.personIds.length),
          total: started.personIds.length,
        });
      }
      setRows(all);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const eligibleCount = preview?.eligible.length ?? 0;
  const typedOk = preview !== null && eligibleCount > 0 && typed.trim() === String(eligibleCount);
  const summary = rows
    ? {
        created: rows.filter((r) => r.result === 'created').length,
        added: rows.filter((r) => r.result === 'added').length,
        suppressed: rows.filter((r) => 'emailSuppressed' in r && r.emailSuppressed).length,
        skipped: rows.filter((r) => r.result === 'skipped').length,
        errors: rows.filter((r) => r.result === 'error').length,
      }
    : null;

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle>{t('people.groupAccess.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          {!rows && (
            <>
              <TextField
                select
                label={t('people.groupAccess.group')}
                value={kind}
                onChange={(e) => {
                  setKind(e.target.value as GroupKind);
                  setTargetId('');
                  reset();
                }}
                disabled={busy}
              >
                {canInviteFaculty && (
                  <MenuItem value="termFaculty">
                    {t('people.groupAccess.kind.termFaculty')}
                  </MenuItem>
                )}
                {canInviteFaculty && (
                  <MenuItem value="faculty">{t('people.groupAccess.kind.faculty')}</MenuItem>
                )}
                <MenuItem value="term">{t('people.groupAccess.kind.term')}</MenuItem>
                <MenuItem value="program">{t('people.groupAccess.kind.program')}</MenuItem>
              </TextField>
              {kind !== 'faculty' && (
                <TextField
                  select
                  label={needsTerm ? t('people.groupAccess.term') : t('people.groupAccess.program')}
                  helperText={
                    kind === 'termFaculty' ? t('people.groupAccess.termFacultyHelp') : undefined
                  }
                  value={targetId}
                  onChange={(e) => {
                    setTargetId(e.target.value);
                    reset();
                  }}
                  disabled={busy}
                >
                  {(needsTerm ? terms : programs).map((x) => (
                    <MenuItem key={x.id as string} value={x.id as string}>
                      {(x.name as string) || (x.id as string)}
                    </MenuItem>
                  ))}
                </TextField>
              )}
              {!preview && (
                <Box>
                  <Button variant="outlined" onClick={loadPreview} disabled={busy || !group()}>
                    {t('people.groupAccess.preview')}
                  </Button>
                </Box>
              )}
            </>
          )}

          {preview && !rows && (
            <>
              <Typography variant="body2">
                {t('people.groupAccess.previewSummary', {
                  total: preview.total,
                  eligible: eligibleCount,
                })}
              </Typography>
              {(preview.withoutRecentSection ?? 0) > 0 && (
                <Alert severity="warning">
                  {t('people.groupAccess.withoutRecentSection', {
                    count: preview.withoutRecentSection ?? 0,
                  })}
                </Alert>
              )}
              {(Object.keys(SKIP_KEYS) as AccessSkipReason[])
                .filter((k) => preview.skipped[k] > 0)
                .map((k) => (
                  <Typography key={k} variant="caption" color="text.secondary">
                    {t(SKIP_KEYS[k], { count: preview.skipped[k] })}
                  </Typography>
                ))}
              {eligibleCount > 0 && (
                <Box
                  sx={{
                    maxHeight: 180,
                    overflow: 'auto',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 1,
                  }}
                >
                  {preview.eligible.map((p) => (
                    <Typography key={p.personId} variant="caption" display="block">
                      {p.name || '—'} · {p.email}
                    </Typography>
                  ))}
                </Box>
              )}
              {eligibleCount > 0 && (
                <TextField
                  label={t('people.groupAccess.typeCount', { count: eligibleCount })}
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  disabled={busy}
                  inputMode="numeric"
                  helperText={t('people.groupAccess.typeCountHelp')}
                />
              )}
              {progress && (
                <Box>
                  <LinearProgress
                    variant="determinate"
                    value={progress.total ? (100 * progress.done) / progress.total : 0}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {t('people.groupAccess.progress', progress)}
                  </Typography>
                </Box>
              )}
            </>
          )}

          {summary && (
            <Alert severity={summary.errors ? 'warning' : 'success'}>
              {t('people.groupAccess.done', summary)}
              {summary.suppressed > 0 && (
                <Box component="span" display="block">
                  {t('people.groupAccess.suppressed', { count: summary.suppressed })}
                </Box>
              )}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={close} disabled={busy}>
          {rows ? t('people.groupAccess.close') : t('people.groupAccess.cancel')}
        </Button>
        {preview && !rows && eligibleCount > 0 && (
          <Button variant="contained" onClick={grant} disabled={busy || !typedOk}>
            {t('people.groupAccess.confirm', { count: eligibleCount })}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
